import Axhelper from "@/lib/Axhelper";

class SettingsService {
    constructor() {
        this.baseUrl = window.RUNTIME_CONFIG.API_URL;
        this.endpoints = {
            getSystem: `/api/settings/getsystem`,
            setSystem: `/api/settings/setsystem`,
            getProjektOpisSchemat: `/api/settings/projektopisschemat`,
            setProjektOpisSchemat: `/api/settings/projektopisschemat`,
            uploadCover: `/api/settings/uploadcover`,
            uploadLogo: `/api/settings/uploadlogo`,
            removeLogo: `/api/settings/removelogo`,
            removeCover: `/api/settings/removecover`,
            deleteCompanyDocuments: `/api/settings/company`,
        };
    }

    async deleteCompanyDocuments(companyId) {
        try {
            if (!companyId) {
                return { success: false, error: 'Company id is required', data: null };
            }
            const url = `${this.endpoints.deleteCompanyDocuments}/${encodeURIComponent(companyId)}/documents`;
            const response = await Axhelper.sendDeleteAsync(url);
            if (response.error) return { success: false, error: response.error, data: null };
            return { success: true, error: null, data: response.data || null };
        } catch (error) {
            console.error('SettingsService.deleteCompanyDocuments error:', error);
            return {
                success: false,
                error: error.message || 'Failed to delete company documents',
                data: null,
            };
        }
    }

    async getSystem() {
        try {
            const response = await Axhelper.sendGetAsync(this.endpoints.getSystem);
            if (response.error) return { data: null, error: response.error };
            return { data: response.data || null, error: null };
        } catch (error) {
            console.error('SettingsService.getSystem error:', error);
            return { data: null, error: error.message || 'Failed to fetch system settings' };
        }
    }

    async setSystem(data) {
        try {
            const response = await Axhelper.sendPutAsync(this.endpoints.setSystem, data);
            if (response.error) return { success: false, error: response.error };
            return { success: true, error: null, data: response.data || null };
        } catch (error) {
            console.error('SettingsService.setSystem error:', error);
            return { success: false, error: error.message || 'Failed to set system settings' };
        }
    }

    async getProjektOpisSchemat() {
        try {
            const response = await Axhelper.sendGetAsync(this.endpoints.getProjektOpisSchemat);
            if (response.error) return { data: null, error: response.error };
            return { data: response.data || { opis: '' }, error: null };
        } catch (error) {
            console.error('SettingsService.getProjektOpisSchemat error:', error);
            return { data: null, error: error.message || 'Failed to fetch project description template' };
        }
    }

    async setProjektOpisSchemat(data) {
        try {
            const response = await Axhelper.sendPutAsync(this.endpoints.setProjektOpisSchemat, data);
            if (response.error) return { success: false, error: response.error };
            return { success: true, error: null, data: response.data || null };
        } catch (error) {
            console.error('SettingsService.setProjektOpisSchemat error:', error);
            return { success: false, error: error.message || 'Failed to save project description template' };
        }
    }

    async uploadCover(file, onUploadProgress) {
        try {
            // Validate file type
            if (!file.type || file.type !== 'image/jpeg') {
                return { success: false, error: `Invalid file type. Only JPEG files are allowed. Uploaded type: ${file.type}` };
            }

            // Create FormData for multipart upload
            const formData = new FormData();
            formData.append('file', file);

            const response = await Axhelper.sendUploadAsync(this.endpoints.uploadCover, formData, onUploadProgress);

            if (response.error) return { success: false, error: response.error };
            return { success: true, error: null, message: response.data || 'Image uploaded successfully.' };
        } catch (error) {
            console.error('SettingsService.uploadCover error:', error);
            return { success: false, error: error.message || 'Failed to upload cover image' };
        }
    }

    getCompanyLogoUrl(companyId, cacheBust = Date.now()) {
        if (!companyId) return null;
        return `${this.baseUrl}/api/settings/getlogo.png?companyId=${encodeURIComponent(companyId)}&t=${cacheBust}`;
    }

    async uploadLogo(file, companyId, onUploadProgress) {
        try {
            if (!companyId) {
                return { success: false, error: 'Company id is required to upload a logo.' };
            }

            // Validate file type - allow PNG and JPEG for logos
            if (!file.type || (!file.type.includes('image/png') && !file.type.includes('image/jpeg'))) {
                return { success: false, error: `Invalid file type. Only PNG and JPEG files are allowed. Uploaded type: ${file.type}` };
            }

            // Validate file size (5MB max)
            if (file.size > 5000 * 1024) {
                return { success: false, error: 'File size too large. Maximum size is 5MB.' };
            }

            // Create FormData for multipart upload
            const formData = new FormData();
            formData.append('file', file);
            formData.append('companyId', companyId);

            const response = await Axhelper.sendUploadAsync(this.endpoints.uploadLogo, formData, onUploadProgress);

            if (response.error) return { success: false, error: response.error };
            return { success: true, error: null, message: response.data || 'Logo uploaded successfully.' };
        } catch (error) {
            console.error('SettingsService.uploadLogo error:', error);
            return { success: false, error: error.message || 'Failed to upload logo image' };
        }
    }

    async removeLogo(companyId) {
        try {
            if (!companyId) {
                return { success: false, error: 'Company id is required to remove a logo.' };
            }

            const response = await Axhelper.sendPostAsync(this.endpoints.removeLogo, { companyId });
            if (response.error) return { success: false, error: response.error };
            return { success: true, error: null, message: response.data || 'Logo removed successfully.' };
        } catch (error) {
            console.error('SettingsService.removeLogo error:', error);
            return { success: false, error: error.message || 'Failed to remove logo' };
        }
    }

    async removeCover() {
        try {
            const response = await Axhelper.sendPostAsync(this.endpoints.removeCover);
            if (response.error) return { success: false, error: response.error };
            return { success: true, error: null, message: response.data || 'Cover removed successfully.' };
        } catch (error) {
            console.error('SettingsService.removeCover error:', error);
            return { success: false, error: error.message || 'Failed to remove cover' };
        }
    }
}

// Export a singleton instance
export default new SettingsService();
